<template>
  <div class="app-sidebar" :class="{ 'collapsed': isMenuCollapsed }" data-test="app-sidebar">

    <!-- Navigation Content -->
    <div class="sidebar-content">
      <!-- Workspace Navigation -->
      <div class="nav-section" data-test="workspace-nav">
        <div class="nav-section-header" data-test="workspace-section-title">
          <span v-if="!isMenuCollapsed" class="section-title">Workspace</span>
          <div class="section-indicator"></div>
        </div>

        <nav class="nav-menu" role="navigation" aria-label="Workspace navigation">
          <router-link 
            to="/dashboard" 
            class="nav-item" 
            :class="{ active: $route.path === '/dashboard' }" 
            data-test="nav-dashboard"
            aria-label="Dashboard"
          >
            <div class="nav-icon">
              <font-awesome-icon icon="chart-pie" />
            </div>
            <span v-if="!isMenuCollapsed" class="nav-label">Dashboard</span>
            <!-- <div v-if="!isMenuCollapsed && $route.path === '/dashboard'" class="active-indicator"></div> -->
          </router-link>

          <router-link 
            to="/apps" 
            class="nav-item" 
            :class="{ active: $route.path && $route.path.startsWith('/apps') }" 
            data-test="nav-apps"
            aria-label="Campaigns"
          >
            <div class="nav-icon">
              <font-awesome-icon icon="mobile-alt" />
            </div>
            <span v-if="!isMenuCollapsed" class="nav-label">Campaigns</span>
            <!-- <div v-if="!isMenuCollapsed && $route.path && $route.path.startsWith('/apps')" class="active-indicator"></div> -->
          </router-link>
        </nav>
      </div>

      <!-- Account Navigation -->
      <div class="nav-section" data-test="account-nav">
        <div class="nav-section-header" data-test="account-section-title">
          <span v-if="!isMenuCollapsed" class="section-title">Account</span>
          <div class="section-indicator"></div>
        </div>

        <nav class="nav-menu" role="navigation" aria-label="Account navigation">
          <router-link 
            to="/my-workspaces" 
            class="nav-item" 
            :class="{ active: $route.path === '/my-workspaces' }" 
            data-test="nav-my-workspaces"
            aria-label="My Workspaces"
          >
            <div class="nav-icon">
              <font-awesome-icon icon="building" />
            </div>
            <span v-if="!isMenuCollapsed" class="nav-label">My Workspaces</span>
            <!-- <div v-if="!isMenuCollapsed && $route.path === '/my-workspaces'" class="active-indicator"></div> -->
          </router-link>

          <router-link 
            to="/my-account" 
            class="nav-item" 
            :class="{ active: $route.path === '/my-account' }" 
            data-test="nav-my-account"
            aria-label="My Account"
          >
            <div class="nav-icon">
              <font-awesome-icon icon="user-circle" />
            </div>
            <span v-if="!isMenuCollapsed" class="nav-label">My Account</span>
            <!-- <div v-if="!isMenuCollapsed && $route.path === '/my-account'" class="active-indicator"></div> -->
          </router-link>

          <button 
            @click="$emit('logout')" 
            class="nav-item logout-btn" 
            data-test="logout-btn"
            aria-label="Logout"
          >
            <div class="nav-icon">
              <font-awesome-icon icon="sign-out-alt" />
            </div>
            <span v-if="!isMenuCollapsed" class="nav-label">Logout</span>
          </button>
        </nav>
      </div>
    </div>

    <!-- Sidebar Footer -->
    <div class="sidebar-footer">
      <!-- Collapse Toggle -->
      <button 
        @click="$emit('toggle-menu')" 
        class="menu-toggle" 
        data-test="menu-toggle"
        :aria-label="isMenuCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      >
        <font-awesome-icon :icon="isMenuCollapsed ? 'chevron-right' : 'chevron-left'" />
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppSidebar',
  
  props: {
    isMenuCollapsed: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['toggle-menu', 'logout']
}
</script>

<style scoped>
/* Modern Sidebar Styles */
.app-sidebar {
  width: 256px;
  background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
  color: #f8fafc;
  display: flex;
  flex-direction: column;
  position: fixed;
  height: calc(100vh - 64px);
  top: 64px;
  left: 0;
  z-index: 998;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  border-right: 1px solid rgba(148, 163, 184, 0.1);
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.15);
}

.app-sidebar.collapsed {
  width: 64px;
}


/* Sidebar Content */
.sidebar-content {
  flex: 1;
  padding: 24px 0 20px;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Navigation Sections */
.nav-section {
  margin-bottom: 32px;
}

.nav-section-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 0 20px;
}

.section-title {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-right: 8px;
}

.section-indicator {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, #64748b 0%, transparent 100%);
}

.collapsed .nav-section-header {
  justify-content: center;
}

.collapsed .section-title {
  display: none;
}

.collapsed .section-indicator {
  display: none;
}

/* Navigation Menu */
.nav-menu {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: #cbd5e1;
  text-decoration: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  position: relative;
  border-radius: 0;
  margin-right: 0;
}

.nav-item:hover {
  background: rgba(59, 130, 246, 0.1);
  color: #f8fafc;
  transform: translateX(4px);
}

.nav-item.active {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  border-radius: 0;
  margin-right: 0;
}

.nav-item.active:hover {
  transform: translateX(4px);
}

.nav-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.nav-icon i {
  font-size: 16px;
  transition: transform 0.2s ease;
}

.nav-item:hover .nav-icon i {
  transform: scale(1.1);
}

.nav-label {
  font-size: 14px;
  font-weight: 500;
  flex: 1;
}

.active-indicator {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 4px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.5);
}

/* Collapsed State */
.collapsed .nav-item {
  justify-content: center;
  margin-right: 0;
  border-radius: 0;
  margin: 0;
}

.collapsed .nav-icon {
  margin-right: 0;
}

.collapsed .nav-label {
  display: none;
}

.collapsed .active-indicator {
  display: none;
}

/* Logout Button */
.logout-btn {
  color: #f87171;
  margin-top: 8px;
}

.logout-btn:hover {
  background: rgba(248, 113, 113, 0.1);
  color: #fca5a5;
}

.logout-btn.active {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

/* Sidebar Footer */
.sidebar-footer {
  padding: 20px 12px 20px 12px;
  border-top: 1px solid rgba(148, 163, 184, 0.1);
}

.menu-toggle {
  width: 100%;
  height: 40px;
  background: rgba(148, 163, 184, 0.1);
  border: 1px solid rgba(148, 163, 184, 0.2);
  color: #94a3b8;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
}

.menu-toggle:hover {
  background: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.3);
  color: #3b82f6;
  transform: translateY(-1px);
}

.collapsed .menu-toggle {
  width: 40px;
  margin: 0 auto;
}

/* Scrollbar Styling */
.sidebar-content::-webkit-scrollbar {
  width: 4px;
}

.sidebar-content::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-content::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.3);
  border-radius: 2px;
}

.sidebar-content::-webkit-scrollbar-thumb:hover {
  background: rgba(148, 163, 184, 0.5);
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .app-sidebar {
    transform: translateX(-100%);
    width: 280px;
    box-shadow: 8px 0 32px rgba(0, 0, 0, 0.25);
  }

  .app-sidebar.open {
    transform: translateX(0);
  }

  .app-sidebar.collapsed {
    transform: translateX(-100%);
    width: 280px;
  }
}

/* Focus States for Accessibility */
.nav-item:focus,
.menu-toggle:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Animation for smooth transitions */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.nav-item {
  animation: slideIn 0.3s ease-out;
}

.nav-item:nth-child(1) { animation-delay: 0.05s; }
.nav-item:nth-child(2) { animation-delay: 0.1s; }
.nav-item:nth-child(3) { animation-delay: 0.15s; }
.nav-item:nth-child(4) { animation-delay: 0.2s; }
</style>
